import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Tag, CaretDown, CaretUp } from "@phosphor-icons/react";
import {
  getBlogBySlug,
  getCategorySlug,
  getRelatedPosts,
} from "@/data/blogData";
import SEO, { blogPostSchema, faqSchema, breadcrumbSchema } from "@/components/SEO";

const relatedResourceMap = {
  "Tax & Compliance": [
    {
      to: "/services/tax-planning",
      label: "Crypto Tax Planning",
      description: "Work with BitGenix on transaction classification, 115BBH planning, and ITR support.",
    },
    {
      to: "/calculators/crypto-tax-calculator-india",
      label: "Crypto Tax Calculator",
      description: "Estimate tax exposure quickly before you file or rebalance your portfolio.",
    },
  ],
  "Investment Strategy": [
    {
      to: "/services/investing-trading",
      label: "Investing & Trading Advisory",
      description: "Build a structured market plan instead of reacting to noise and volatility.",
    },
    {
      to: "/services/portfolio-management",
      label: "Portfolio Management",
      description: "Turn strategy into an ongoing allocation and review process.",
    },
  ],
  "Business Advisory": [
    {
      to: "/services/development",
      label: "Blockchain Development",
      description: "Move from article-level ideas into architecture, smart contracts, and product delivery.",
    },
    {
      to: "/services/tokenization-launch",
      label: "Tokenization & Launch Advisory",
      description: "Stress-test token design, launch readiness, and business execution before going live.",
    },
  ],
  Regulation: [
    {
      to: "/services/licensing-compliance",
      label: "Licensing & Compliance",
      description: "Get help with FIU-IND registration, PMLA controls, and operational compliance.",
    },
    {
      to: "/contact",
      label: "Book a Compliance Consultation",
      description: "Talk through your setup, documents, and regulatory roadmap with the BitGenix team.",
    },
  ],
  "Crypto Education": [
    {
      to: "/tools",
      label: "Free Crypto Tools",
      description: "Pair educational reading with calculators and practical decision-support tools.",
    },
    {
      to: "/contact",
      label: "Ask BitGenix",
      description: "Get a second opinion before acting on a new crypto idea or investment concept.",
    },
  ],
  Security: [
    {
      to: "/services/security-custody",
      label: "Security & Custody Advisory",
      description: "Design a safer custody setup, backup process, and inheritance plan.",
    },
    {
      to: "/contact",
      label: "Review Your Security Setup",
      description: "Book a consultation to pressure-test your current storage and recovery process.",
    },
  ],
};

function slugifyHeading(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function BlogFAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  const answerId = `faq-${slugifyHeading(q)}`;

  return (
    <div className="border border-[#383A46]/10 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left p-5 flex items-start justify-between gap-4"
        aria-expanded={open}
        aria-controls={answerId}
        data-testid={`blog-faq-${q.slice(0, 15).replace(/\s/g, "-").toLowerCase()}`}
      >
        <span className="font-body font-medium text-base text-[#21324D]">{q}</span>
        {open ? <CaretUp size={16} className="text-[#A7A55C] flex-shrink-0 mt-1" /> : <CaretDown size={16} className="text-[#383A46] flex-shrink-0 mt-1" />}
      </button>
      <div id={answerId} hidden={!open} className="px-5 pb-5">
        <p className="font-body text-sm text-[#383A46] leading-relaxed">{a}</p>
      </div>
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getBlogBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F2]">
        <SEO
          title="Article Not Found"
          description="The BitGenix article you requested could not be found."
          path={`/blog/${slug}`}
          robots="noindex, nofollow"
        />
        <div className="text-center">
          <h1 className="font-heading text-3xl text-[#21324D] mb-4">Article not found</h1>
          <Link to="/blog" className="font-body text-[#A7A55C] hover:underline">Back to blog</Link>
        </div>
      </div>
    );
  }

  const relatedPosts = getRelatedPosts(post, 3);
  const categorySlug = getCategorySlug(post.category);
  const faqItems = post.content.filter((block) => block.type === "faq").flatMap((block) => block.items);
  const tableOfContents = post.content
    .filter((block) => block.type === "heading")
    .map((block) => ({ ...block, id: slugifyHeading(block.text) }));
  const relatedResources = relatedResourceMap[post.category] || [
    {
      to: "/contact",
      label: "Talk to BitGenix",
      description: "Discuss your specific situation with a BitGenix advisor.",
    },
  ];

  const jsonLdSchemas = [
    blogPostSchema(post),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: post.category, path: `/blog/category/${categorySlug}` },
      { name: post.title, path: `/blog/${slug}` },
    ]),
  ];

  if (faqItems.length > 0) {
    jsonLdSchemas.push(faqSchema(faqItems));
  }

  return (
    <div data-testid={`blog-post-${slug}`}>
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${slug}`}
        type="article"
        keywords={post.tags}
        includeFeed
        article={{
          date: post.date,
          modifiedDate: post.modifiedDate,
          author: post.author,
          tags: post.tags,
          section: post.category,
        }}
        jsonLd={jsonLdSchemas}
      />

      <section className="bg-[#21324D] py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <Link
            to={categorySlug ? `/blog/category/${categorySlug}` : "/blog"}
            className="font-body text-sm text-[#F5F5F2]/50 hover:text-[#A7A55C] transition-colors inline-flex items-center gap-2 mb-8"
            data-testid="blog-back-link"
          >
            <ArrowLeft size={14} /> Back to {post.category}
          </Link>
          <header>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <Link
                to={`/blog/category/${categorySlug}`}
                className="font-body text-xs uppercase tracking-[0.15em] text-[#A7A55C] font-medium bg-[#A7A55C]/10 px-3 py-1"
              >
                {post.category}
              </Link>
              <span className="font-body text-sm text-[#F5F5F2]/50 flex items-center gap-1.5">
                <Clock size={14} /> {post.readTime}
              </span>
            </div>
            <h1
              className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#F5F5F2] leading-tight tracking-tight mb-6"
              data-testid="blog-post-heading"
            >
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-[#F5F5F2]/50 font-body text-sm mb-6">
              <span>{post.author}</span>
              <span className="w-1 h-1 rounded-full bg-[#F5F5F2]/30" />
              <span>Published {post.date}</span>
              <span className="w-1 h-1 rounded-full bg-[#F5F5F2]/30" />
              <span>Updated {post.modifiedDate || post.date}</span>
            </div>
            <p className="font-body text-lg text-[#F5F5F2]/75 leading-relaxed max-w-3xl">
              {post.excerpt}
            </p>
          </header>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-[#F5F5F2]">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 lg:grid lg:grid-cols-[240px,minmax(0,1fr)] lg:gap-14">
          <aside className="mb-12 lg:mb-0 lg:sticky lg:top-28 self-start">
            {tableOfContents.length > 0 && (
              <div className="border border-[#383A46]/10 bg-white p-6 mb-6">
                <p className="font-body text-xs uppercase tracking-[0.15em] text-[#383A46]/50 mb-4">
                  On This Page
                </p>
                <nav className="space-y-3" aria-label="Table of contents">
                  {tableOfContents.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className="block font-body text-sm text-[#383A46] hover:text-[#A7A55C] transition-colors leading-relaxed"
                    >
                      {item.text}
                    </a>
                  ))}
                </nav>
              </div>
            )}

            <div className="border border-[#383A46]/10 bg-white p-6">
              <p className="font-body text-xs uppercase tracking-[0.15em] text-[#383A46]/50 mb-4">
                Related Resources
              </p>
              <div className="space-y-4">
                {relatedResources.map((resource) => (
                  <Link key={resource.to} to={resource.to} className="block group">
                    <p className="font-body font-medium text-sm text-[#21324D] group-hover:text-[#A7A55C] transition-colors">
                      {resource.label}
                    </p>
                    <p className="font-body text-xs text-[#383A46]/65 leading-relaxed mt-1">
                      {resource.description}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </aside>

          <div>
            <article className="space-y-8" data-testid="blog-post-content">
              {post.content.map((block, index) => {
                if (block.type === "heading") {
                  const headingId = slugifyHeading(block.text);
                  return (
                    <h2
                      key={headingId}
                      id={headingId}
                      className="scroll-mt-28 font-heading font-semibold text-2xl sm:text-3xl text-[#21324D] tracking-tight leading-tight pt-4"
                    >
                      {block.text}
                    </h2>
                  );
                }

                if (block.type === "paragraph") {
                  return (
                    <p key={`${block.type}-${index}`} className="font-body text-base text-[#383A46] leading-[1.8]">
                      {block.text}
                    </p>
                  );
                }

                if (block.type === "faq") {
                  return (
                    <section key={`${block.type}-${index}`} className="space-y-3 pt-4" data-testid="blog-faq-section">
                      <h3 className="font-body font-medium text-lg text-[#21324D] mb-4">Frequently Asked Questions</h3>
                      {block.items.map((faq, faqIndex) => (
                        <BlogFAQ key={`${faq.q}-${faqIndex}`} q={faq.q} a={faq.a} />
                      ))}
                    </section>
                  );
                }

                return null;
              })}
            </article>

            {post.tags && (
              <div className="mt-12 pt-8 border-t border-[#383A46]/10">
                <div className="flex flex-wrap items-center gap-2">
                  <Tag size={16} className="text-[#383A46]/40" />
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="font-body text-xs text-[#383A46]/60 bg-[#383A46]/5 px-3 py-1.5"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 grid gap-4 md:grid-cols-2">
              {relatedResources.map((resource) => (
                <Link
                  key={`${resource.to}-card`}
                  to={resource.to}
                  className="border border-[#383A46]/10 bg-white p-6 group hover:border-[#A7A55C] transition-colors"
                >
                  <p className="font-body font-medium text-base text-[#21324D] group-hover:text-[#A7A55C] transition-colors">
                    {resource.label}
                  </p>
                  <p className="font-body text-sm text-[#383A46] leading-relaxed mt-2">
                    {resource.description}
                  </p>
                </Link>
              ))}
            </div>

            <div className="mt-16 bg-[#152F2C] p-10 sm:p-12" data-testid="blog-inline-cta">
              <h3 className="font-heading font-semibold text-2xl text-[#F5F5F2] mb-3">
                Need help with this topic?
              </h3>
              <p className="font-body text-base text-[#F5F5F2]/70 mb-6">
                BitGenix provides expert consulting on everything covered in this article. Book a free consultation to discuss your specific situation.
              </p>
              <Link
                to="/contact"
                className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-colors duration-300 font-body font-semibold text-sm tracking-wide px-8 py-4 rounded-none inline-flex items-center gap-2"
                data-testid="blog-post-cta"
              >
                Book a Consultation <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {relatedPosts.length > 0 && (
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <h2 className="font-body font-medium text-sm uppercase tracking-[0.15em] text-[#383A46]/50 mb-10">
              Continue Reading
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <Link
                  to={`/blog/${relatedPost.slug}`}
                  key={relatedPost.slug}
                  className="group border border-[#383A46]/10 p-6 sm:p-8 transition-all duration-300 hover:border-[#A7A55C]"
                  data-testid={`related-post-${relatedPost.slug}`}
                >
                  <span className="font-body text-xs uppercase tracking-[0.15em] text-[#A7A55C] font-medium">
                    {relatedPost.category}
                  </span>
                  <h3 className="font-body font-medium text-base text-[#21324D] mt-3 mb-3 group-hover:text-[#A7A55C] transition-colors leading-snug">
                    {relatedPost.title}
                  </h3>
                  <p className="font-body text-sm text-[#383A46] leading-relaxed mb-4 line-clamp-3">
                    {relatedPost.excerpt}
                  </p>
                  <span className="font-body text-xs text-[#383A46]/50">{relatedPost.date}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
